import { useState } from "react";
import { Card, Col, Row, Layout, Typography, Progress, Space } from "antd";
import Button from "@mui/material/Button";
import PlotlyStockChart from "../../charts/Plotly/PlotlyStockChart";
import { realizeTargetPath } from "../../utils/math";
import { createFutureDateRange } from "../../utils/datetime";

const { Title } = Typography;

const RiskLevels = [
  {
    title: "Very Low",
    riskValue: 25,
  },
  {
    title: "Moderate",
    riskValue: 50,
  },
  {
    title: "High Risk",
    riskValue: 75,
  },
  {
    title: "Very Risky",
    riskValue: 100,
  },
];

const RiskBanner = ({ selectedKey }) => {
  const riskData = RiskLevels[selectedKey - 1];

  return (
    <Space direction="vertical" style={{ textAlign: 'center', marginBottom: 24 }}>
      <Title level={4}>{riskData.title}</Title>
      <Progress
        percent={riskData.riskValue}
        status="active"
        showInfo={false}
        strokeColor={{
          '0%': '#52c41a',
          '100%': '#ff4d4f',
        }}
      />
    </Space>
  );
};

const RiskScenario = ({
  targetUpside,
  targetDownside,
  onClick,
  isSelected = false,
  nYears = 5,
  initialValue = 10000,
  minY,
  maxY,
}) => {
  const upsidePath = realizeTargetPath(initialValue, nYears, 252, targetUpside);
  const downsidePath = realizeTargetPath(initialValue, nYears, 252, targetDownside);
  const futureDates = createFutureDateRange(nYears * 252, "days");
  const downsideChartData = futureDates.map((date, i) => [date, downsidePath[i]]);
  const upsideChartData = futureDates.map((date, i) => [date, upsidePath[i]]);

  return (
    <Card
      bordered={false}
      className="cursor gapless"
      onClick={onClick}
      style={{
        border: isSelected ? "1px solid #1890ff" : "1px solid #e8e8e8",
        height: 315,
        cursor: "pointer",
        transition: "border 0.3s",
      }}
    >
      <div style={{ height: 300, width: "100%", padding: 10 }}>
        <PlotlyStockChart
          timeseriesData={[
            { name: "Upside", data: upsideChartData, color: "#4ee1ac", fill: "toself" },
            { name: "Downside", data: downsideChartData, color: "#f54040", fill: "toself" },
          ]}
          disableScrollbar={true}
          description={`Upside: ${targetUpside * 100}%, Downside: ${targetDownside * 100}%`}
          {...{ minY, maxY }}
          normalize={false}
          valueFormat={"$,.0f"}
          disableAnnotations={true}
        />
      </div>
    </Card>
  );
};

const InteractiveRisk = () => {
  const [initialValue] = useState(10000);
  const [nYears] = useState(5);
  const [minY] = useState(initialValue / 2);
  const [maxY] = useState(initialValue * 2);
  const [selectedKey, setSelectedKey] = useState("1");
  const [showResult, setShowResult] = useState(false);

  const handleSubmit = () => {
    setShowResult(true);
  };

  const commonProps = { minY, maxY, nYears };

  return (
    <Layout style={{ margin: 'auto', maxWidth: 1200 }}>
      <Row justify="center" style={{ marginBottom: 24 }}>
        <RiskBanner selectedKey={selectedKey} />
      </Row>

      <Row gutter={[32, 16]} justify="center">
        {RiskLevels.map((_, idx) => (
          <Col xs={24} md={12} key={idx}>
            <RiskScenario
              key={String(idx + 1)}
              targetUpside={0.15 + idx * 0.15}
              targetDownside={-0.11 - idx * 0.11}
              onClick={() => setSelectedKey(String(idx + 1))}
              isSelected={selectedKey === String(idx + 1)}
              {...commonProps}
            />
          </Col>
        ))}
      </Row>

      <Row justify="center" style={{ marginTop: 32 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          style={{ padding: '10px 20px', fontSize: '16px' }}
        >
          Continue
        </Button>
      </Row>

      {showResult && (
        <Row justify="center" style={{ marginTop: 32 }}>
          <Col xs={24} md={20}>
            <Card>
              <Title level={4}>Selected Risk Scenario Results</Title>
              <p>
                You selected <strong>{RiskLevels[selectedKey - 1].title}</strong> risk.
                Based on this selection, we predict the following potential portfolio outcomes.
              </p>
              <p>Upside Potential: {((0.15 + (selectedKey - 1) * 0.15) * 100).toFixed(1)}%</p>
              <p>Downside Risk: {((-0.11 - (selectedKey - 1) * 0.11) * 100).toFixed(1)}%</p>
            </Card>
          </Col>
        </Row>
      )}
    </Layout>
  );
};

export default InteractiveRisk;
